<template>
    <div class="form-submitted-modal">
        <GoalModal
            data-qa="form-submitted-modal-goal"
            :title="$t('funnel.edit.goal.form.submitted.title')"
            :is-open="isOpen"
            :summary-text="$t('funnel.edit.goal.form.submitted.summary')"
            alert-type="critical"
            :show-warning="showWarningFormDeleted"
            :warning-text="$t('funnel.edit.goal.form.submitted.deleted.error')"
            @close-modal="closeModal"
            @update-config="updateConfig"
        >
            <template #goalParameters>
                <section class="form-submitted-select">
                    <DsMultiselect
                        v-model="selectedFormId"
                        data-qa="form-submitted-select"
                        :loading="loadingDropdown"
                        allow-add
                        bind-value-only
                        required
                        searchable
                        show-search-icon
                        label-prop="title"
                        :add-text="$t('funnel.edit.goal.form.submitted.new.form')"
                        value-prop="id"
                        :options="forms"
                        :force-invalid="emptyForm"
                        :placeholder="$t('funnel.edit.goal.form.submitted.select')"
                        @add="addForm"
                    >
                        <template v-if="emptyForm" #help>
                            {{ $t('global.field.required') }}
                        </template>
                    </DsMultiselect>
                    <DsOutlineButton
                        v-if="isFormSelected && !showWarningFormDeleted"
                        data-qa="form-edit"
                        trailing-icon="external-link"
                        class="edit-form"
                        @click="editForm"
                    >
                        {{ $t('funnel.edit.goal.form.submitted.edit.form') }}
                    </DsOutlineButton>
                </section>
            </template>
        </GoalModal>
    </div>
</template>

<script>
import GoalModal from '../goalModal/GoalModal.vue';
import {
    loadAllForms,
} from './smart.forms.api';
import groupBy from 'lodash.groupby';

export default {
    components: {
        GoalModal,
    },
    props: {
        isOpen: {
            type: Boolean,
            required: true,
        },
        funnelName: {
            type: String,
            default: '',
        },
        appInfo: {
            type: Object,
            required: true,
        },
        cell: {
            type: Object,
            default: () => ({
                smartFormInstanceId: '',
                name: '',
            }),
        },
    },

    emits: ['close-modal', 'update-cell-value'],

    data() {
        return {
            forms: [],
            ungroupedForms: [],
            selectedFormId: undefined,
            selectedForm: {},
            loadingDropdown: true,
            showWarningFormDeleted: false,
            newFormWasCreated: false,
            emptyForm: false,
        };
    },

    computed: {
        isSpaApp() {
            return this.appInfo?.appEditionFlags?.spaApp;
        },

        isFormSelected() {
            return this.selectedFormId !== undefined;
        },

        createFormLink() {
            return `${this.appInfo.spaBaseUrl}/forms/new/public`;
        },
    },

    watch: {
        selectedFormId: {
            handler(val) {
                if (val) {
                    this.showWarningFormDeleted = false;
                }
            },
        },
    },

    async mounted() {
        await this.loadFormsDropdown();

        if (!this.isFormSelected && this.cell?.value?.smartFormInstanceId) {
            this.loadValues(this.cell?.value?.smartFormInstanceId.toString());
        }
        window.addEventListener('focus', this.handleFocus);
    },

    unmounted() {
        window.removeEventListener('focus', this.handleFocus);
    },

    methods: {
        async handleFocus() {
            await this.loadFormsDropdown();
        },

        loadValues(selectedValue) {
            if (this.existsForm(selectedValue)) {
                this.selectedFormId = selectedValue;
            } else {
                this.selectedFormId = undefined;
                this.showWarningFormDeleted = true;
            }
        },

        updateConfig() {
            try {
                if (this.isFormSelected) {
                    const cell = { ...this.cell };

                    cell.value = cell.value || {};
                    cell.value.smartFormInstanceId = this.selectedFormId;

                    this.$emit('update-cell-value', { cell });
                } else {
                    this.emptyForm = true;
                }
            } catch (error) {
                this.$error({ message: this.$t('funnel.edit.goal.form.submitted.error.saving') });
            }
        },

        closeModal() {
            this.$emit('close-modal');
        },

        async addForm() {
            await window.open(this.createFormLink);

            this.newFormWasCreated = true;
            await this.loadFormsDropdown();
        },

        editForm() {
            const selectedForm = this.ungroupedForms?.filter((form) => form.id === this.selectedFormId)[0];

            window.open(`${this.appInfo.spaBaseUrl}/forms/${selectedForm.audience?.toLowerCase()}/${selectedForm.id}/edit/build?app_id=${selectedForm.accountId}`);
        },

        groupForms(allForms) {
            const groupedForms = groupBy(allForms, ({ audience }) => {
                return audience === 'INTERNAL' ? this.$t('funnel.edit.goal.internal.forms') : this.$t('funnel.edit.goal.public.forms');
            });

            return Object.keys(groupedForms).map((id) => ({
                label: id,
                options: groupedForms[id].map((item) => ({
                    id: item.id,
                    title: item.title,
                    icon: 'form',
                })),
            }));
        },

        sortForms(groupedForms) {
            return groupedForms.sort((a, b) => (a.label === this.$t('funnel.edit.goal.public.forms') ? -1 : b.label === this.$t('funnel.edit.goal.public.forms') ? 1 : 0));
        },

        async loadFormsDropdown() {
            try {
                this.loadingDropdown = true;
                const allForms = await loadAllForms();

                const newForms = allForms.filter((form) => {
                    return !this.ungroupedForms.some((existingForm) => existingForm.id === form.id);
                });

                this.ungroupedForms = allForms;

                this.forms = this.sortForms(this.groupForms(allForms));

                if (newForms.length === 1 && this.newFormWasCreated) {
                    this.newFormWasCreated = false;
                    this.selectedFormId = newForms[0].id;
                }

                if (this.isFormSelected) {
                    if (!this.existsForm(this.selectedFormId)) {
                        this.selectedFormId = undefined;
                        this.showWarningFormDeleted = true;
                    }
                }
            } catch (error) {
                this.$error({ message: this.$t('funnel.edit.goal.form.submitted.error.loading') });
            }
            this.loadingDropdown = false;
        },

        existsForm(formId) {
            return this.ungroupedForms.filter((form) => form.id === formId).length > 0;
        },
    },
};
</script>

<style lang="scss" scoped>
.edit-form {
    width: px-to-rem(192);
    height: px-to-rem(40);
    margin-left: $spacing-200;
}
.form-submitted-select {
    display: flex;
}
</style>
